import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Sparkles } from 'lucide-react';
import type { SubscriptionPlan } from '../../types/subscription';
import { SubscriptionPlanIconGlyph, subscriptionPlanBadgeStyle } from '../../utils/subscriptionPlanIcon';
import { colorWithAlpha } from '../../utils/colorUtils';
import { formatPlanDurationLabel, formatPlanPriceLabel } from '../../utils/formatPlanDurationLabel';
import { useAppLocale } from '../../providers/LocaleProvider';

export interface SubscriptionPlanCardProps {
  plan: SubscriptionPlan;
  isCurrent?: boolean;
  prominenceBadge?: string | null;
  highlighted?: boolean;
  actionLabel: string;
  onAction?: () => void;
  disabled?: boolean;
  features?: string[];
  featuresTitle?: string;
  className?: string;
  /** Tighter layout for profile subscription tab */
  compact?: boolean;
}

function resolvePlanAccent(color?: string | null): string {
  if (!color) return '#8b5cf6';
  const trimmed = color.trim();
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

/** Decorative blobs + rings — variant rotates layout so grid cards feel distinct. */
function PlanCardAbstractBackground({
  accent,
  isCurrent,
  variant,
}: {
  accent: string;
  isCurrent: boolean;
  variant: number;
}) {
  const decorAccent = isCurrent ? '#10b981' : accent;
  const blobA = colorWithAlpha(decorAccent, isCurrent ? 0.28 : 0.34);
  const blobB = colorWithAlpha(decorAccent, isCurrent ? 0.16 : 0.2);
  const blobC = colorWithAlpha(decorAccent, isCurrent ? 0.1 : 0.14);

  const blobAClass =
    variant === 1
      ? 'absolute -end-20 -top-24 h-48 w-48'
      : variant === 2
        ? 'absolute -end-12 top-8 h-40 w-40'
        : 'absolute -end-16 -top-16 h-44 w-44';
  const blobBClass =
    variant === 1
      ? 'absolute -bottom-20 -start-14 h-36 w-36'
      : variant === 2
        ? 'absolute -bottom-10 start-6 h-28 w-28'
        : 'absolute -bottom-14 -start-12 h-32 w-32';
  const ringClass =
    variant === 1
      ? 'absolute end-8 top-[4.5rem] h-[4.5rem] w-[4.5rem]'
      : variant === 2
        ? 'absolute end-10 top-20 h-16 w-16'
        : 'absolute end-6 top-24 h-20 w-20';

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div
        className="absolute inset-0 dark:hidden"
        style={{
          background: isCurrent
            ? 'linear-gradient(148deg, rgba(16,185,129,0.16) 0%, rgba(255,255,255,0.97) 44%, rgba(52,211,153,0.09) 100%)'
            : `linear-gradient(148deg, ${colorWithAlpha(accent, 0.17)} 0%, rgba(255,255,255,0.96) 44%, ${colorWithAlpha(accent, 0.07)} 100%)`,
        }}
      />
      <div
        className="absolute inset-0 hidden dark:block"
        style={{
          background: isCurrent
            ? 'linear-gradient(148deg, rgba(16,185,129,0.14) 0%, rgba(17,24,39,0.94) 46%, rgba(6,78,59,0.12) 100%)'
            : `linear-gradient(148deg, ${colorWithAlpha(accent, 0.2)} 0%, rgba(17,24,39,0.93) 46%, ${colorWithAlpha(accent, 0.1)} 100%)`,
        }}
      />

      <div className={`${blobAClass} rounded-full blur-3xl opacity-90`} style={{ background: blobA }} />
      <div className={`${blobBClass} rounded-full blur-2xl opacity-70`} style={{ background: blobB }} />
      <div
        className="absolute start-[18%] top-[62%] h-24 w-24 rounded-full blur-2xl opacity-50"
        style={{ background: blobC }}
      />

      <div
        className={`${ringClass} rounded-full border-[2.5px] opacity-[0.14] dark:opacity-[0.1]`}
        style={{ borderColor: decorAccent }}
      />
      <div
        className={`${ringClass} rounded-full border opacity-[0.07] dark:opacity-[0.05]`}
        style={{ borderColor: decorAccent, transform: 'scale(1.45)' }}
      />

      {variant !== 2 ? (
        <div
          className="absolute start-5 top-[38%] h-2 w-2 rounded-full opacity-25"
          style={{ backgroundColor: decorAccent }}
        />
      ) : null}
      {variant === 0 ? (
        <div
          className="absolute start-10 top-[42%] h-1.5 w-1.5 rounded-full opacity-15"
          style={{ backgroundColor: decorAccent }}
        />
      ) : null}

      <div
        className="absolute -end-6 bottom-[28%] h-14 w-14 rotate-12 rounded-2xl opacity-[0.07] dark:opacity-[0.05]"
        style={{ backgroundColor: decorAccent }}
      />

      <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.45)_0%,transparent_40%,transparent_58%,rgba(255,255,255,0.1)_100%)] dark:bg-[linear-gradient(125deg,rgba(255,255,255,0.05)_0%,transparent_42%,transparent_58%,rgba(0,0,0,0.18)_100%)]" />
    </div>
  );
}

export const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
  plan,
  isCurrent = false,
  prominenceBadge = null,
  highlighted = false,
  actionLabel,
  onAction,
  disabled = false,
  features,
  featuresTitle,
  className = '',
  compact = false,
}) => {
  const { t } = useTranslation('subscription');
  const { locale } = useAppLocale();

  const accent = resolvePlanAccent(plan.color);
  const durationLabel = formatPlanDurationLabel(plan.duration, t, locale);
  const priceLabel = formatPlanPriceLabel(plan.price, locale);
  const perPeriodLabel = t('planPricePer', { duration: durationLabel });

  const cardStyle = useMemo(
    () =>
      ({
        '--plan-accent': accent,
        '--plan-accent-soft': colorWithAlpha(accent, 0.12),
        '--plan-accent-glow': colorWithAlpha(accent, 0.22),
      }) as React.CSSProperties,
    [accent]
  );

  const showTopBadge = isCurrent || Boolean(prominenceBadge);
  const topBadgeLabel = isCurrent ? null : prominenceBadge;
  const decorVariant = plan.id % 3;

  return (
    <article
      className={[
        'group relative isolate flex h-full flex-col overflow-hidden rounded-2xl border shadow-sm backdrop-blur-sm transition-all duration-300',
        isCurrent
          ? 'border-emerald-400/90 ring-2 ring-emerald-400/25 shadow-md shadow-emerald-500/10 dark:border-emerald-500/70'
          : highlighted
            ? 'border-[color:var(--plan-accent)]/50 shadow-lg shadow-[color:var(--plan-accent-glow)] hover:-translate-y-0.5 hover:shadow-xl dark:border-[color:var(--plan-accent)]/40'
            : 'border-gray-200/90 hover:-translate-y-0.5 hover:border-[color:var(--plan-accent)]/35 hover:shadow-lg hover:shadow-[color:var(--plan-accent-glow)] dark:border-gray-700/90',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={cardStyle}
      aria-labelledby={`subscription-plan-${plan.id}-title`}
    >
      <PlanCardAbstractBackground accent={accent} isCurrent={isCurrent} variant={decorVariant} />

      <div
        className="relative z-[1] h-1.5 w-full shrink-0"
        style={{
          background: isCurrent
            ? 'linear-gradient(90deg, rgb(52 211 153), rgb(16 185 129))'
            : `linear-gradient(90deg, ${colorWithAlpha(accent, 0.95)}, ${colorWithAlpha(accent, 0.45)})`,
        }}
        aria-hidden
      />

      {showTopBadge && (
        <div className="absolute top-4 end-4 z-10 flex flex-col items-end gap-1.5">
          {isCurrent && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-200">
              <Check className="h-3 w-3" aria-hidden />
              {t('planCurrentBadge')}
            </span>
          )}
          {topBadgeLabel && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm"
              style={{ backgroundColor: accent }}
            >
              <Sparkles className="h-3 w-3" aria-hidden />
              {topBadgeLabel}
            </span>
          )}
        </div>
      )}

      <div className={`relative z-[1] flex flex-1 flex-col ${compact ? 'p-4 sm:p-4' : 'p-5 sm:p-6'}`}>
        <div className={`flex items-start gap-3 pe-14 ${compact ? 'mb-3' : 'mb-5 gap-4 pe-16'}`}>
          <div
            className={`flex shrink-0 items-center justify-center rounded-2xl border shadow-sm ${compact ? 'h-11 w-11' : 'h-14 w-14'}`}
            style={subscriptionPlanBadgeStyle(plan.color)}
          >
            <SubscriptionPlanIconGlyph
              icon={plan.icon ?? null}
              color={plan.color ?? null}
              className={compact ? 'h-5 w-5' : 'h-7 w-7'}
            />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h4
              id={`subscription-plan-${plan.id}-title`}
              className={`font-bold leading-tight text-gray-900 dark:text-gray-100 ${compact ? 'text-base' : 'text-lg'}`}
            >
              {plan.name}
            </h4>
            <p className={`mt-0.5 font-medium text-[color:var(--plan-accent)] ${compact ? 'text-xs' : 'text-sm'}`}>{durationLabel}</p>
          </div>
        </div>

        <div
          className={`rounded-xl border backdrop-blur-sm ${compact ? 'mb-3 px-3 py-2.5' : 'mb-5 px-4 py-3.5'}`}
          style={{
            borderColor: colorWithAlpha(accent, 0.22),
            backgroundColor: isCurrent ? 'rgba(16, 185, 129, 0.1)' : 'var(--plan-accent-soft)',
          }}
        >
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className={`font-bold tracking-tight text-gray-900 dark:text-white ${compact ? 'text-2xl' : 'text-3xl'}`}>{priceLabel}</span>
          </div>
          <p className={`mt-0.5 font-medium text-gray-600 dark:text-gray-400 ${compact ? 'text-[11px]' : 'text-xs'}`}>{perPeriodLabel}</p>
        </div>

        {plan.description ? (
          <p className={`mb-3 leading-relaxed text-gray-600 dark:text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>{plan.description}</p>
        ) : null}

        {features && features.length > 0 ? (
          <div className={`flex-1 ${compact ? 'mb-3' : 'mb-5'}`}>
            {featuresTitle ? (
              <p className={`mb-2 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${compact ? 'text-[10px]' : 'text-xs'}`}>
                {featuresTitle}
              </p>
            ) : null}
            <ul className={compact ? 'space-y-1.5' : 'space-y-2'}>
              {features.map((feature) => (
                <li key={feature} className={`flex items-start gap-2 text-gray-600 dark:text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>
                  <Check className={`shrink-0 text-emerald-500 ${compact ? 'mt-0.5 h-3.5 w-3.5' : 'mt-0.5 h-4 w-4'}`} aria-hidden />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex-1" />
        )}

        <button
          type="button"
          disabled={disabled}
          onClick={onAction}
          className={[
            `mt-auto w-full rounded-xl font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${compact ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm'}`,
            disabled
              ? 'cursor-default bg-white/70 text-gray-500 backdrop-blur-sm dark:bg-gray-800/80 dark:text-gray-400'
              : highlighted
                ? 'text-white shadow-md hover:brightness-110 focus-visible:ring-[color:var(--plan-accent)]'
                : 'border border-white/60 bg-white/75 text-gray-900 backdrop-blur-sm hover:border-[color:var(--plan-accent)]/40 hover:bg-white/90 dark:border-gray-600/80 dark:bg-gray-800/75 dark:text-gray-100 dark:hover:bg-gray-800/90',
          ].join(' ')}
          style={
            !disabled && highlighted
              ? { backgroundColor: accent, boxShadow: `0 10px 24px ${colorWithAlpha(accent, 0.28)}` }
              : undefined
          }
        >
          {actionLabel}
        </button>
      </div>
    </article>
  );
};

export default SubscriptionPlanCard;
